/**
 * Unwraps ```markdown...``` code fences that are embedded inline in content
 * (e.g. inside list items). The backend sometimes wraps extracted document text
 * in these fences; leaving them causes empty gray code blocks in MarkdownRenderer.
 *
 * Only targets the `markdown` language tag — other code blocks (python, js, etc.) are preserved.
 */
export function unwrapInlineMarkdownFences(text: string): string {
  // Replace closed ```markdown ... ``` blocks with their inner content
  let result = text.replace(/```markdown\n([\s\S]*?)```[ \t]*(\n|$)/g, '$1\n');

  // Remove any remaining unclosed ```markdown opening markers
  result = result.replace(/```markdown\n?/g, '');

  return result;
}

/**
 * Removes consecutive blocks of lines starting with `|` that do NOT contain
 * a proper table separator row (e.g. `| --- | --- |`). These are orphaned table
 * row fragments produced by PDF extraction and look broken when rendered.
 */
export function removeOrphanedTableFragments(text: string): string {
  const lines = text.split('\n');
  const result: string[] = [];
  let i = 0;

  while (i < lines.length) {
    if (lines[i].trimStart().startsWith('|')) {
      const block: string[] = [];

      while (i < lines.length && lines[i].trimStart().startsWith('|')) {
        block.push(lines[i]);
        i++;
      }

      const hasSeparator = block.some((l) => /^\s*\|[\s|:.-]+\|\s*$/.test(l));

      if (hasSeparator) {
        result.push(...block);
      }
    } else {
      result.push(lines[i]);
      i++;
    }
  }

  return result.join('\n');
}

type ExtractedLoaderContent = {
  titleText: string | null;
  bodyContent: string;
};

/**
 * Prepares raw loader text for display: unwraps code fences, strips inline
 * markdown fences, then splits into a title (first line, heading markers removed)
 * and optional body (remaining lines).
 */
export function extractLoaderContent(text: string | null | undefined): ExtractedLoaderContent {
  if (!text) {
    return { titleText: null, bodyContent: '' };
  }

  const display = unwrapInlineMarkdownFences(unwrapMarkdownCodeFence(text));
  const newlineIdx = display.indexOf('\n');
  const firstLine = (newlineIdx === -1 ? display : display.slice(0, newlineIdx)).trim();
  const titleText = firstLine.replace(/^#{1,6}\s+/, '').trim() || null;
  const bodyContent = newlineIdx !== -1 ? display.slice(newlineIdx + 1).trim() : '';

  return { titleText, bodyContent };
}

/**
 * If the text is wrapped in a single markdown code fence (``` ... ```),
 * returns the inner content so it can be rendered as markdown instead of raw code.
 * Used for loader/status text that may arrive from the backend as a code block.
 */
export function unwrapMarkdownCodeFence(text: string): string {
  const trimmed = text.trim();
  const openFence = /^```(\w*)\s*\n?/;
  const match = trimmed.match(openFence);

  if (!match) {
    return text;
  }

  const afterOpen = trimmed.slice(match[0].length);
  const closeFence = /\n?```\s*$/;

  if (!closeFence.test(afterOpen)) {
    return text;
  }

  return afterOpen.replace(closeFence, '').trim();
}
